import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DiagnosisProgress from './DiagnosisProgress';
import AnalysisRecovery from './AnalysisRecovery';
import AnalysisProgressLink from './AnalysisProgressLink';
import ClarificationCard from './ClarificationCard';
import ReadingConfirmCard from './ReadingConfirmCard';
import ConfirmForEveryone from './ConfirmForEveryone';
import DefinitionCaptureCard from './DefinitionCaptureCard';
import AnswerCard, { isDataAnswer, panelHoldsRelated, whyPerspectiveOf } from './AnswerCard';
import CatalogAnswerCard from './CatalogAnswerCard';
import DocumentCard from './DocumentCard';
import SpecialistAnswerCard from './SpecialistAnswerCard';
import RouteChip from './RouteChip';
import EarlierLine from './EarlierLine';
import DefaultsLine from './DefaultsLine';
import ReadingLine, { ReadAsLine, readingShowsDefaults } from './ReadingLine';
import FeedbackBar from './FeedbackBar';
import { SidePanelSlot } from './simple/ResultPanel';
import { tookOf } from '../../stores/slices/progress';
import ProgressLine from './ProgressLine';
import DoneLine from './DoneLine';
import AnswerFrame, { NotAcceptedLine } from './AnswerFrame';
import ScopeChip from './ScopeChip';
import ObjectiveLine from './ObjectiveLine';
import PriorWorkCard from './PriorWorkCard';
import PriorWorkListCard from './PriorWorkListCard';
import { useSupportRef } from '../../support/SupportRef';
import { wasAway, type LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { isProgressAnswer } from '../../stores/slices/progress';
import { stromaCompactPanel } from './askPresentation';
import WorkingBlock, { WorkingSteps } from './simple/WorkingBlock';
import AnswerActions, { FollowUpChips } from './simple/AnswerActions';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { useComposerReply } from './simple/composerReply';

// #1508 (flag on): how the answer ran and the route, scope and objective chips are one compact tail of the row, shown
// while the turn is hovered or holds focus (Change stays one click, and Tab reaches it); where nothing hovers (touch),
// always shown
const CONTEXT_LINE = {
  display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5, minWidth: 0,
  opacity: 0, transition: 'opacity .12s',
  '&:empty': { display: 'none' },
  '@media (hover: none)': { opacity: 1 },
  '& .MuiChip-root': { height: 20, fontSize: 11.5 },
  '& .MuiChip-label': { px: 0.875 },
  '& .MuiButton-root': { py: 0, minHeight: 0, fontSize: 12 },
} as const;

/** The row's own slots (rows, not boxes): empty ones take no room. */
const ROW = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5, minWidth: 0, '&:empty': { display: 'none' } } as const;

export default function TurnBlock({ turn, isLatest }: { turn: LandingAgentTurn; isLatest: boolean }) {
  // what this turn holds, however it ended (#472): a failed or stopped turn shows no answer card (and so no
  // Trace link), yet a support link from it must still reach its run — its trace, and a walk's run itself
  useSupportRef('session', turn.sessionId);
  useSupportRef('occurrence', turn.diagnosisProgress?.id ? `diagnosis_run:${turn.diagnosisProgress.id}` : null);
  // #1506: the live stages are the "Working" block and the answer ends in one action row
  const selectedPerspectiveId = useVoyagerStore((s) => s.selectedPerspectiveId);
  const answerRef = React.useRef<HTMLDivElement | null>(null);
  const [rowAt, setRowAt] = React.useState<HTMLElement | null>(null);
  const [panelAt, setPanelAt] = React.useState<HTMLElement | null>(null);
  const [workedOut, setWorkedOut] = React.useState(false);
  const [correctRequest, setCorrectRequest] = React.useState(0);
  const canCorrect = !!(turn.result?.diagnose?.target?.metric && whyPerspectiveOf(turn, selectedPerspectiveId));
  // a data answer's Correct this (#1508) replies to it from the thread's composer, prefilled
  const threadId = useVoyagerStore((s) => s.currentThreadId);
  const answerFailed = useVoyagerStore((s) => !!s.currentThreadId && s.threadAnswers?.[s.currentThreadId] === 'failed');
  const loadThread = useVoyagerStore((s) => s.loadThread);
  const startReply = useComposerReply((s) => s.startReply);
  const canReplyCorrect = !canCorrect && !!threadId && turn.status === 'done' && !turn.document && isDataAnswer(turn.result);
  // F4 (#1858): a running answer is one progress line (a walk keeps its own); a finished one F4 timed, one Done line —
  // the step list is behind "How this was worked out" in both
  const running = turn.status === 'running';
  const progressing = running && !turn.diagnosisOperationId && !turn.document;
  const done = !running && isProgressAnswer(turn) && ['done', 'error', 'cancelled'].includes(turn.status);
  // a finished data answer's clean layout: its context line (the reading, the period, the note) above it; one quiet row
  // (Data · SQL · Trace · Explore | copy · good · bad · ask again · More, then the time) under it, the steps in Trace;
  // the route, scope and objective controls in More
  const clean = !running && !turn.document && isDataAnswer(turn.result);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const sidePanel = React.useContext(SidePanelSlot);
  const took = clean ? tookOf(turn) : null;
  const context = clean ? null : <>
    <RouteChip turn={turn} />
    <ScopeChip turn={turn} />
    <ObjectiveLine turn={turn} />
  </>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5,
      '&:hover [data-turn-icons], &:focus-within [data-turn-icons], &:hover [data-turn-context], &:focus-within [data-turn-context]': { opacity: 1 } }}>
      <Box
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '80%',
          px: 2,
          py: 1,
          bgcolor: 'var(--you, var(--sunk))',
          color: 'text.primary',
          borderRadius: '14px',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{turn.userText}</Typography>
      </Box>
      {/* listed without its answer (#1270): the thread's open rebuilds it */}
      {turn.answerPending && !answerFailed && (
        <Typography variant="body2" color="text.secondary">Loading the answer…</Typography>
      )}
      {/* its read failed (#2188): said, with the way to read it again */}
      {turn.answerPending && answerFailed && (
        <Alert severity="warning" variant="outlined" sx={stromaCompactPanel} data-testid="answer-load-failed"
          action={<Button color="inherit" size="small" onClick={() => threadId && loadThread(threadId)}>Try again</Button>}>
          This answer couldn't be loaded.
        </Alert>
      )}
      {!turn.answerPending && <>
      {/* diagnose turns run zero LLM steps — 'Agent activity · 0 steps'
          is misleading there; the trace shows its own activity log */}
      {turn.diagnosisOperationId && <DiagnosisProgress turn={turn} />}
      {turn.analysisOperationId && <AnalysisRecovery turn={turn} />}
      {/* only after the asker left the running turn and came back (§5): never while they watch, through any reconnect */}
      {running && !turn.diagnosisOperationId && wasAway(turn) && (
        <Alert severity="info" sx={stromaCompactPanel} data-testid="away-banner">This kept running while you were away. <AnalysisProgressLink turn={turn} /></Alert>
      )}
      {/* what the question was understood as (§2): the plan's reading, then what ran */}
      {clean ? <ReadAsLine turn={turn} /> : <ReadingLine turn={turn} />}
      {/* the objective a question was asked inside (#766), said; Track this is in More */}
      {clean && <ObjectiveLine turn={turn} track={false} />}
      {!clean && !turn.document && !(turn.diagnosisOperationId && running) && !progressing && !done && <WorkingBlock turn={turn} />}
      {/* A turn can fail before it produces anything at all — a rejected
          handshake never reaches the pipeline. Without this the turn renders
          as nothing, which reads as still working. */}
      {turn.status === 'error' && turn.error && !turn.diagnosisDisconnected && (
        // a choice on a reading card that was already made (#1174 P3b, a 409) is no failure: said calmly
        <Alert severity={turn.errorStatus === 409 ? 'info' : 'error'} variant="outlined">{turn.error}</Alert>
      )}
      {/* the "Related" line an answer names is its panel's Context tab (#1500) */}
      {!panelHoldsRelated(turn) && <PriorWorkCard turn={turn} />}
      {turn.clarification && <ClarificationCard turn={turn} />}
      {/* the answer gate's reading card (#1174 P3b): confirm by option id; no free text, no skip */}
      {turn.readingConfirmation && <ReadingConfirmCard turn={turn} />}
      {/* after a "Yes, that's what I mean" (#1174 §7): Studio's confirmation, for a person who may give it */}
      <ConfirmForEveryone turn={turn} />
      {turn.definitionRequest && <DefinitionCaptureCard turn={turn} />}
      {/* the earlier answer of the thread this one used (#1807), said above the answer, with Not that one */}
      <EarlierLine turn={turn} />
      {/* every default this answer stated (#1808), above the answer, each with one click to change it */}
      {!clean && <DefaultsLine turn={turn} chips={!readingShowsDefaults(turn)} />}
      {/* the answer itself, whichever card says it: what the action row's Copy takes (#1506); no box of its own */}
      <Box ref={answerRef} data-testid="turn-answer" sx={{ display: 'contents' }}>
      {/* a question about what exists (#761) answers in its own card; the answer card renders nothing for it */}
      <CatalogAnswerCard turn={turn} />
      {/* knowledge, help and the strict decline (#764) answer in their own card; the answer card renders nothing for them */}
      <SpecialistAnswerCard turn={turn} />
      {/* earlier analyses asked for explicitly (#911), with what people said beside them, in their own card */}
      <PriorWorkListCard turn={turn} />
      {/* an attached file is answered by its numbers and the question back (#763), not an answer card */}
      {turn.document ? <DocumentCard turn={turn} /> : <AnswerCard turn={turn} simple correctRequest={correctRequest} slots={{ row: rowAt, panel: sidePanel ?? panelAt }} />}
      </Box>
      {/* the answer's shape before its numbers (§4), and a part code did not accept (§4a) */}
      <AnswerFrame turn={turn} />
      <NotAcceptedLine turn={turn} />
      {progressing && <ProgressLine turn={turn} />}
      {/* the answer's one quiet row: Explore · Data · SQL · Trace, the icons (Claude's: always on the last reply, on hover
          of an earlier one), then how it ran and its route and scope on hover */}
      <Box data-testid="reply-row" sx={{ ...ROW, minHeight: 24, display: running ? 'none' : 'flex',
        ...(clean ? { columnGap: 0, ml: -1, '& [data-row-tabs]:empty + [data-row-divider]': { display: 'none' } } : {}) }}>
        <Box ref={setRowAt} data-row-tabs sx={ROW} />
        {clean && <Box data-row-divider aria-hidden sx={{ width: '1px', height: 16, bgcolor: 'var(--line)', mx: 0.75 }} />}
        <Box data-turn-icons sx={{ ...ROW, opacity: isLatest ? 1 : 0, transition: 'opacity .12s', '@media (hover: none)': { opacity: 1 } }}>
          <AnswerActions turn={turn} answerRef={answerRef} answer={clean}
            onBad={clean && turn.sessionId ? () => setFeedbackOpen(true) : undefined}
            onCorrect={canCorrect ? () => setCorrectRequest((n) => n + 1)
              : canReplyCorrect ? () => startReply(threadId as string, turn.id) : undefined} />
        </Box>
        {clean ? (
          took != null && <Box component="span" data-testid="answer-took" title="How long this answer took"
            sx={{ ml: 'auto', fontSize: 12, color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>{took} s</Box>
        ) : (
          <Box data-turn-context data-testid="turn-context-line" sx={{ ...CONTEXT_LINE, ml: 'auto' }}>
            {done && <DoneLine turn={turn} open={workedOut} onToggle={() => setWorkedOut((v) => !v)} />}
            {context}
          </Box>
        )}
      </Box>
      {/* the thumbs down's "What went wrong?" — today's feedback form, under the row */}
      {clean && feedbackOpen && <FeedbackBar turn={turn} onClose={() => setFeedbackOpen(false)} />}
      {!clean && done && workedOut && <WorkingSteps turn={turn} />}
      <Box ref={setPanelAt} sx={{ minWidth: 0, '&:empty': { display: 'none' } }} />
      <FollowUpChips turn={turn} />
      </>}
    </Box>
  );
}
